import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

// Import Leaflet and fix icon issues
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const buildStreetLine1 = (address: any, displayName?: string) => {
  const a: any = address || {};
  const street =
    a.road ||
    a.pedestrian ||
    a.cycleway ||
    a.footway ||
    a.path ||
    a.residential ||
    a.neighbourhood ||
    a.suburb ||
    a.city_district ||
    a.county ||
    '';
  const house = a.house_number || a.house || '';
  const combined = `${String(street).trim()} ${String(house).trim()}`.trim();
  if (combined) return combined;

  const dn = String(displayName || '').trim();
  if (!dn) return '';
  // Fallback: take first 2 parts (often includes street + number)
  const parts = dn.split(',').map((p) => p.trim()).filter(Boolean);
  return parts.slice(0, 2).join(', ');
};

interface AddressMapViewProps {
  onBack: () => void;
  onAddressSelect: (address: {
    address_line_1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  }) => void;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
}

interface MapClickHandlerProps {
  onLocationSelect: (lat: number, lng: number) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

interface MapViewControllerProps {
  center?: [number, number] | null;
  zoom?: number;
}

const MapViewController: React.FC<MapViewControllerProps> = ({ center, zoom = 16 }) => {
  const map = useMap();

  useEffect(() => {
    if (!center) return;
    map.flyTo(center, zoom, { animate: true, duration: 0.9 });
  }, [center, zoom, map]);

  return null;
};

const AddressMapView: React.FC<AddressMapViewProps> = ({
  onBack,
  onAddressSelect,
  onClose,
  onToggleFullscreen,
  isFullscreen = false,
}) => {
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [currentLocation, setCurrentLocation] = useState<[number, number] | null>(null);
  const [addressDetails, setAddressDetails] = useState<any>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Default center (New York City)
  const defaultCenter: [number, number] = [40.7128, -74.0060];

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setGeoError(null);
          setCurrentLocation([latitude, longitude]);
          setSelectedPosition([latitude, longitude]);
          getAddressFromCoordinates(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
          setGeoError(
            error.code === 1
              ? 'Location permission denied. Enable it in your browser to use “My Location”.'
              : 'Could not access your location. Please try again.'
          );
        }
      );
    }
  };

  // Search for addresses using Nominatim (OpenStreetMap)
  const searchAddresses = async (query: string) => {
    const q = query.trim();
    if (q.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=8&addressdetails=1`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching addresses:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Reverse geocoding to get address from coordinates
  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    setIsLoadingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();
      setAddressDetails(data);
    } catch (error) {
      console.error('Error getting address:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Handle location selection
  const handleLocationSelect = (lat: number, lng: number) => {
    setSelectedPosition([lat, lng]);
    getAddressFromCoordinates(lat, lng);
  };

  // Handle search result selection
  const handleSearchResultSelect = (result: any) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setSelectedPosition([lat, lng]);
    setAddressDetails(result);
    setSearchResults([]);
    setSearchQuery(result.display_name);
  };

  // Handle address confirmation
  const handleConfirmAddress = () => {
    if (!selectedPosition || !addressDetails) return;

    const address = addressDetails.address || {};
    const addressLine1 = buildStreetLine1(address, addressDetails.display_name);
    const fullAddress = {
      address_line_1: addressLine1,
      city: address.city || address.town || address.village || '',
      state: address.state || address.province || '',
      postal_code: address.postcode || '',
      country: address.country || '',
      latitude: selectedPosition[0],
      longitude: selectedPosition[1],
    };

    onAddressSelect(fullAddress);
  };

  // Search on Enter key
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchAddresses(searchQuery);
    }
  };

  // Debounced, Google-like search suggestions while typing
  useEffect(() => {
    const q = searchQuery.trim();
    const t = window.setTimeout(() => setDebouncedQuery(q), 320);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!debouncedQuery) {
      setSearchResults([]);
      return;
    }
    searchAddresses(debouncedQuery);
  }, [debouncedQuery]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with back button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 2, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        background: 'linear-gradient(135deg, rgba(124,58,237,0.10) 0%, rgba(2,132,199,0.08) 55%, rgba(255,255,255,0.85) 100%)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <IconButton 
            onClick={onBack}
            sx={{ 
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #7c3aed 0%, #0284c7 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Find Address on Map
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onToggleFullscreen && (
            <IconButton
              onClick={onToggleFullscreen}
              sx={{
                borderRadius: 2.5,
                border: '1px solid rgba(0,0,0,0.10)',
                background: 'rgba(255,255,255,0.8)',
                '&:hover': { background: 'rgba(255,255,255,0.95)' },
              }}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
          <IconButton
            onClick={onClose || onBack}
            sx={{
              borderRadius: 2.5,
              border: '1px solid rgba(0,0,0,0.10)',
              background: 'rgba(255,255,255,0.8)',
              '&:hover': { background: 'rgba(255,255,255,0.95)' },
            }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Map container */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: '400px',
          m: 2,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 18px 55px rgba(2,132,199,0.14)',
          background: 'rgba(255,255,255,0.85)',
        }}
      >
        {!mapLoaded && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(2,132,199,0.06) 100%)',
            zIndex: 1000
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading map...
              </Typography>
            </Box>
          </Box>
        )}
        
        <MapContainer
          center={currentLocation || defaultCenter}
          zoom={13}
          style={{ height: '100%', width: '100%', minHeight: '400px' }}
          whenReady={() => setMapLoaded(true)}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapClickHandler onLocationSelect={handleLocationSelect} />

          <MapViewController
            center={selectedPosition || currentLocation}
            zoom={selectedPosition || currentLocation ? 16 : 13}
          />
          
          {/* Current location marker */}
          {currentLocation && (
            <Marker position={currentLocation}>
              <Popup>
                <Typography variant="body2">Your current location</Typography>
              </Popup>
            </Marker>
          )}
          
          {/* Selected location marker */}
          {selectedPosition && (
            <Marker position={selectedPosition}>
              <Popup>
                <Typography variant="body2">
                  {isLoadingAddress ? 'Loading address...' : 'Selected location'}
                </Typography>
              </Popup>
            </Marker>
          )}
        </MapContainer>

        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            zIndex: 1250,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 999,
              border: '1px solid rgba(0,0,0,0.10)',
              background: 'rgba(255,255,255,0.92)',
              boxShadow: '0 18px 55px rgba(17,24,39,0.14)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search for an address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearching && <CircularProgress size={18} />}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  background: 'transparent',
                  minHeight: 42,
                  px: 0.5,
                },
                '& .MuiInputBase-input': {
                  py: 1,
                },
                '& fieldset': { border: 'none' },
              }}
            />
          </Paper>

          {geoError && (
            <Alert severity="warning" sx={{ mt: 1, borderRadius: 3 }}>
              {geoError}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 1,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.10)',
                background: 'rgba(255,255,255,0.96)',
                boxShadow: '0 18px 55px rgba(17,24,39,0.14)',
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {searchResults.map((result, index) => (
                <Box
                  key={index}
                  onClick={() => handleSearchResultSelect(result)}
                  sx={{
                    p: 1.25,
                    cursor: 'pointer',
                    borderBottom: index < searchResults.length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                    '&:hover': {
                      background: 'rgba(124,58,237,0.08)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(2,132,199,0.10) 100%)',
                        border: '1px solid rgba(124,58,237,0.14)',
                        flex: '0 0 auto',
                      }}
                    >
                      <LocationIcon fontSize="small" color="primary" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 950, lineHeight: 1.25 }} noWrap>
                        {result.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} noWrap>
                        {[result?.address?.city || result?.address?.town || result?.address?.village, result?.address?.country]
                          .filter(Boolean)
                          .join(' • ')}
                      </Typography>
                    </Box>
                    {index < 3 && (
                      <Chip
                        size="small"
                        label="Top"
                        sx={{
                          fontWeight: 900,
                          height: 22,
                          background: 'linear-gradient(135deg, rgba(124,58,237,0.16) 0%, rgba(2,132,199,0.12) 100%)',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>
          )}
        </Box>

        {/* Current location button */}
        <Button
          variant="contained"
          startIcon={<MyLocationIcon />}
          onClick={getCurrentLocation}
          sx={{
            position: 'absolute',
            bottom: 88,
            right: 16,
            zIndex: 1200,
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: '0 10px 30px rgba(124,58,237,0.28)',
            '&:hover': {
              background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 14px 40px rgba(124,58,237,0.34)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          My Location
        </Button>

        {(selectedPosition || isLoadingAddress) && (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              left: 12,
              right: 12,
              bottom: 12,
              zIndex: 1250,
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.08)',
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(124,58,237,0.06) 40%, rgba(2,132,199,0.05) 100%)',
              boxShadow: '0 18px 55px rgba(17,24,39,0.18)',
              p: 1.25,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.14) 0%, rgba(2,132,199,0.12) 100%)',
                  border: '1px solid rgba(124,58,237,0.18)',
                  flex: '0 0 auto',
                }}
              >
                <LocationIcon color="primary" />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                  {isLoadingAddress ? 'Looking up address…' : 'Selected location'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.35 }}>
                  {isLoadingAddress
                    ? 'Please wait a moment.'
                    : (addressDetails?.display_name || 'Click on the map to refine the pin.')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 1.1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedPosition(null);
                  setAddressDetails(null);
                  setSearchQuery('');
                }}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 900,
                  borderColor: 'rgba(0,0,0,0.16)',
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                disabled={!addressDetails || isLoadingAddress}
                onClick={handleConfirmAddress}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 1000,
                  py: 1,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  boxShadow: '0 10px 30px rgba(124,58,237,0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
                    boxShadow: '0 14px 40px rgba(124,58,237,0.32)',
                  },
                }}
              >
                Use this address
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Address details and confirmation */}
      {selectedPosition && (
        <Box sx={{ p: 2, pt: 0, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          {isLoadingAddress ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading address details...</Typography>
            </Box>
          ) : addressDetails ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Selected Address
                </Typography>
              </Box>
              
              <Paper sx={{ p: 2, mb: 2, background: 'rgba(102, 126, 234, 0.05)' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {addressDetails.display_name}
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedPosition(null);
                    setAddressDetails(null);
                    setSearchQuery('');
                  }}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  Clear Selection
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmAddress}
                  sx={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Use This Address
                </Button>
              </Box>
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Click on the map to select a location, or search for an address above.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AddressMapView;
